import {useEffect, useRef, useState} from 'react'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import {useNavigate} from 'react-router-dom'
import {useRosFeatures} from '../access/useRosFeatures'
import {config} from '../config/environment'
import {SLACK_LINK_TOKEN_KEY} from '../slackLinkToken'
import {SERIF_HEADING} from '../theme'

type LinkState = 'linking' | 'linked' | 'failed'

export function SlackConnectPage() {
  const {authFetch} = useRosFeatures()
  const navigate = useNavigate()
  const started = useRef(false)
  const [state, setState] = useState<LinkState>('linking')
  const [message, setMessage] = useState('Confirming your Ros account…')

  useEffect(() => {
    if (started.current) return
    started.current = true
    let token = ''
    try { token = sessionStorage.getItem(SLACK_LINK_TOKEN_KEY) ?? '' } catch { /* handled below */ }
    if (!token) {
      setState('failed')
      setMessage('This Slack connection link is missing or has expired. Return to Slack and request a new one.')
      return
    }

    void (async () => {
      try {
        const response = await authFetch(`${config.API_CF_URL}/api/r3/slack/link`, {
          method: 'POST',
          headers: {'Content-Type': 'application/json'},
          body: JSON.stringify({token}),
        })
        if (!response.ok) {
          const body = await response.json().catch(() => null) as {error?: string} | null
          throw new Error(body?.error || 'Ros could not connect this Slack account.')
        }
        try { sessionStorage.removeItem(SLACK_LINK_TOKEN_KEY) } catch { /* one-time server token is already consumed */ }
        setState('linked')
        setMessage('Your Ros and Slack accounts are connected. Return to Slack and send your message again.')
      } catch (error) {
        setState('failed')
        setMessage(error instanceof Error ? error.message : 'Ros could not connect this Slack account.')
      }
    })()
  }, [authFetch])

  return (
    <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100%', p: 3}}>
      <Paper elevation={0} sx={{width: '100%', maxWidth: 480, p: 4, textAlign: 'center', border: '1px solid', borderColor: 'divider', borderRadius: 3}}>
        <Typography variant="h5" sx={{...SERIF_HEADING, mb: 1}}>Connect Ros to Slack</Typography>
        {state === 'linking' ? (
          <>
            <CircularProgress size={28} sx={{my: 2}} />
            <Typography color="text.secondary">{message}</Typography>
          </>
        ) : (
          <>
            <Alert severity={state === 'linked' ? 'success' : 'error'} sx={{my: 2, textAlign: 'left'}}>{message}</Alert>
            <Button variant="contained" onClick={() => navigate('/')}>Continue to Ros</Button>
          </>
        )}
      </Paper>
    </Box>
  )
}
